import { memo, useCallback, useRef, useState } from 'react'
import { IVersionHistoryItem } from 'store/types/aiVideo'
import AIPromptText from 'components/AIPromptText'
import AISkeletonItem from 'components/AISkeletonItem'
import { AiGenerationLoadingContent } from 'components/AIGenerationLoadingContent'
import { formatVideoDuration } from 'components/RecentlyUsed/VideoItem/utils'
import ActionsOverlay from 'common-components/ActionsOverlay'
import RegenerateIcon from 'assets/svgr-icons/Regenerate.svg'
import DownloadIcon from 'assets/svgr-icons/Download.svg'
import { translate } from 'utils/translations'
import { downloadMedia } from 'utils'
import { T } from 'consts/translationKeys'
import { DEFAULT_MEDIA_DURATION, PROJECT_MEDIA_TYPES } from 'consts'
import { SUB_SIDEBAR_KEYS } from 'consts/sidebar'
import { TabKey } from 'containers/SubSidebar/AIVideoContent/consts'
import { useAddAiItemToTimeline } from 'hooks/useAddAiItemToTimeline'
import { useGenerationProgress } from 'hooks/useGenerationProgress'
import { dispatch } from 'store'
import { aiVideoThunks } from 'store/features/aiVideo/thunks'
import { aiImageThunks } from 'store/features/aiImage/thunks'
import { editorThunks } from 'store/features/editor/thunks'
import { SQUARE_ASPECT_RATIO, normalizeAspectRatio } from 'utils/aspectRatio'
import useAutoPlayWhenVisible from '../../components/VideoItem/useAutoPlayWhenVisible'
import * as Styled from './styled'

interface IVersionHistoryItemProps {
  item: IVersionHistoryItem
}

const VersionHistoryItem = ({ item }: IVersionHistoryItemProps) => {
  const [isLoading, setIsLoading] = useState(true)
  const containerRef = useRef<HTMLDivElement>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const addItemToTimeline = useAddAiItemToTimeline()

  const isProcessing = !!item.process

  const { progress, message } = useGenerationProgress({
    assetId: isProcessing ? item.id : undefined,
    isGenerating: isProcessing
  })

  useAutoPlayWhenVisible(containerRef, videoRef, isLoading)

  const handleLoad = () => setIsLoading(false)

  const handleMouseEnter = () => {
    if (item.type === 'video' && videoRef.current && !isLoading) {
      videoRef.current.play()
    }
  }

  const handleMouseLeave = () => {
    if (item.type === 'video' && videoRef.current) {
      videoRef.current.pause()
      videoRef.current.currentTime = 0
    }
  }

  const handleAddToTimeline = useCallback(() => {
    if (item.type === 'video') {
      addItemToTimeline({
        type: PROJECT_MEDIA_TYPES.aiVideo,
        path: item.url,
        fileName: `ai-video-${item.id}.mp4`,
        originalWidth: item.width ?? 0,
        originalHeight: item.height ?? 0,
        duration: item.durationSeconds ?? 0,
        assetId: String(item.id)
      })
    } else {
      addItemToTimeline({
        type: PROJECT_MEDIA_TYPES.aiImage,
        path: item.url,
        fileName: `ai-image-${item.assetId}`,
        originalWidth: item.width ?? 0,
        originalHeight: item.height ?? 0,
        duration: DEFAULT_MEDIA_DURATION,
        assetId: String(item.assetId),
        assetParentId: String(item.id)
      })
    }
  }, [addItemToTimeline, item])

  const handleRegenerate = useCallback(() => {
    if (item.type === 'video') {
      dispatch(aiVideoThunks.setGenerationParamsByAssetId(Number(item.id)))
      dispatch(aiVideoThunks.setActiveTab(TabKey.GENERATE))
    } else {
      if (!item.assetId) return
      dispatch(aiImageThunks.setGenerationParamsByAssetId(Number(item.assetId)))
      dispatch(editorThunks.setActiveSidebarTab(SUB_SIDEBAR_KEYS.AI_IMAGE))
    }
  }, [item])

  const handleDownload = useCallback(async () => {
    const fileName =
      item.type === 'video'
        ? `ai-video-${item.id}`
        : `ai-image-${item.assetId ?? item.id}`
    await downloadMedia({ filePath: item.url, fileName })
  }, [item])

  return (
    <Styled.VersionItemContainer ref={containerRef}>
      {item.prompt && <AIPromptText promptText={item.prompt} />}
      {(item.modelName ||
        item.modelQuality ||
        (item.type === 'video' && item.durationSeconds !== null)) && (
        <Styled.MetadataBadges>
          {item.modelName && (
            <Styled.MetadataBadge>{item.modelName}</Styled.MetadataBadge>
          )}
          {item.modelQuality && (
            <Styled.MetadataBadge>{item.modelQuality}</Styled.MetadataBadge>
          )}
          {item.type === 'video' && item.durationSeconds !== null && (
            <Styled.MetadataBadge>
              {formatVideoDuration(item.durationSeconds ?? 0)}
            </Styled.MetadataBadge>
          )}
        </Styled.MetadataBadges>
      )}
      {isProcessing ? (
        <AiGenerationLoadingContent
          overlay
          progress={progress}
          message={message}
          aspectRatio={normalizeAspectRatio(
            item.aspectRatio || SQUARE_ASPECT_RATIO
          )}
        />
      ) : (
        <Styled.MediaWrapper
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {isLoading &&
            (item.type === 'video' && item.thumbnailUrl ? (
              <Styled.ThumbnailImage src={item.thumbnailUrl} />
            ) : (
              <AISkeletonItem width="100%" height="100%" />
            ))}
          {item.type === 'video' ? (
            <Styled.VideoElement
              ref={videoRef}
              src={item.url}
              onLoadedData={handleLoad}
              $isLoading={isLoading}
              crossOrigin="anonymous"
              muted
              loop
              playsInline
            />
          ) : (
            <Styled.ImageElement
              src={item.url}
              onLoad={handleLoad}
              $isLoading={isLoading}
            />
          )}
          {!isLoading && (
            <ActionsOverlay
              actions={{
                mainAction: handleAddToTimeline,
                primaryActions: [
                  {
                    key: T.RE_GENERATE,
                    title: translate(T.RE_GENERATE),
                    icon: <RegenerateIcon />,
                    onClick: handleRegenerate
                  },
                  {
                    key: T.DOWNLOAD,
                    title: translate(T.DOWNLOAD),
                    icon: <DownloadIcon />,
                    onClick: handleDownload
                  }
                ]
              }}
            />
          )}
        </Styled.MediaWrapper>
      )}
    </Styled.VersionItemContainer>
  )
}

export default memo(VersionHistoryItem)
